import React, { useContext } from 'react';
import { Container, Row, Col, Card, Button, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { UserContext } from '../Contexts/UserContext';
import { Helmet } from 'react-helmet';

const OurServicesPage = () => {
    const { user } = useContext(UserContext);
    const styles = {
        general: {
            fontFamily: "'Inter', sans-serif",
            color: '#2C3E50',
        },
        hero: {
            background: 'linear-gradient(135deg, #1a365d 0%, #2C5282 100%)',
            borderRadius: '16px',
            padding: '4rem 2rem',
            color: 'white',
            marginBottom: '4rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
        heroTitle: {
            fontSize: '3.5rem',
            fontWeight: '700',
            marginBottom: '1rem',
            background: 'linear-gradient(45deg, #ffffff 30%, #e2e8f0 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
        },
        heroSubtitle: {
            fontSize: '1.2rem',
            lineHeight: '1.8',
            opacity: '0.9',
            maxWidth: '800px',
            margin: '0 auto',
        },
        card: {
            border: 'none',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            height: '100%',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            },
        },
        cardHeader: {
            padding: '1.5rem',
            border: 'none',
        },
        cardTitle: {
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '1.5rem',
            color: '#1a365d',
        },
        cardText: {
            fontSize: '1.1rem',
            lineHeight: '1.8',
            color: '#4A5568',
            marginBottom: '2rem',
        },
        badge: {
            padding: '0.5rem 1rem',
            fontSize: '0.9rem',
            fontWeight: '500',
            marginRight: '0.5rem',
            marginBottom: '0.5rem',
        },
        featureList: {
            margin: '2rem 0',
        },
        featureItem: {
            marginBottom: '1rem',
            fontSize: '1.1rem',
            color: '#4A5568',
            display: 'flex',
            alignItems: 'center',
        },
        button: {
            padding: '1rem 2rem',
            fontWeight: '600',
            borderRadius: '8px',
            transition: 'transform 0.2s ease',
            '&:hover': {
                transform: 'translateY(-2px)',
            },
        },
        cta: {
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            borderRadius: '16px',
            padding: '3rem',
            border: 'none',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        },
    };

    // eslint-disable-next-line no-unused-vars
    const fadeInUp = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    return (
        <>
            <Helmet>
                <title>DNACloudHub | Services</title>
                <meta name="description" content="Discover the range of services offered by DNACloudHub, including cloud-based infrastructure for running qpAdm models and advanced genetic analysis tools for researchers and geneticists." />
                <meta property="og:title" content={'DNACloudHub | Services'} />
                <meta property="og:description" content={'"Discover the range of services offered by DNACloudHub, including cloud-based infrastructure for running qpAdm models and advanced genetic analysis tools for researchers and geneticists."'} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`https://dnacloudhub.com/services`} />
            </Helmet>

            <Container fluid className="px-4" style={styles.general}>
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                >
                    <Container className="my-5">
                        <div style={styles.hero} className="text-center">
                            <h1 style={styles.heroTitle}>Our Services</h1>
                            <p style={styles.heroSubtitle}>
                                We offer cutting-edge solutions for your DNA analysis and computational needs. Learn more about how we can help you with our custom-built infrastructure and AI-powered tools.
                            </p>
                        </div>
                    </Container>
                </motion.div>

                {/* Services Section */}
                <Container className="mb-5">
                    <Row className="justify-content-center g-4">
                        {/* Cloud Hosting Service */}
                        <Col md={6} lg={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <Card style={styles.card}>
                                    <Card.Header style={{ ...styles.cardHeader, background: 'linear-gradient(135deg, #2C5282 0%, #1a365d 100%)' }} className="text-white">
                                        <h4 className="mb-0 fw-bold">qpadm Models Running In The Cloud</h4>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <h3 style={styles.cardTitle}>Run Your Custom Models</h3>
                                        <p style={styles.cardText}>
                                            We provide cloud-based infrastructure to run your custom qpadm models seamlessly. Scale as you grow and analyze your genetic data with the power of cloud computing. Whether you're running small tests or large-scale analysis, our platform gives you the flexibility you need.
                                        </p>
                                        <div style={styles.featureList}>
                                            <div style={styles.featureItem}>
                                                <Badge bg="primary" style={styles.badge}>Scalable</Badge>
                                                Cloud Infrastructure
                                            </div>
                                            <div style={styles.featureItem}>
                                                <Badge bg="primary" style={styles.badge}>Custom Models</Badge>
                                                Tailored for Your Needs
                                            </div>
                                            <div style={styles.featureItem}>
                                                <Badge bg="primary" style={styles.badge}>Secure</Badge>
                                                Data Storage and Privacy
                                            </div>
                                        </div>
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            as={Link}
                                            to={user && user.uuid ? "/run_qpadm" : "/signup"}
                                            style={styles.button}
                                            className="w-100 shadow"
                                        >
                                            Get Started
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>

                        {/* AI Analysis Tool */}
                        <Col md={6} lg={4}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                            >
                                <Card style={styles.card}>
                                    <Card.Header style={{ ...styles.cardHeader, background: 'linear-gradient(135deg, #D69E2E 0%, #B7791F 100%)' }} className="text-white">
                                        <h4 className="mb-0 fw-bold">AI DNA Analysis Tool</h4>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <h3 style={styles.cardTitle}>Comprehensive DNA Breakdown</h3>
                                        <p style={styles.cardText}>
                                            Our AI-powered tool will analyze your genetic data and provide a comprehensive, easy-to-understand breakdown of your DNA. Understand your ancestry, traits, and potential health risks, all with advanced machine learning algorithms.
                                        </p>
                                        <div style={styles.featureList}>
                                            <div style={styles.featureItem}>
                                                <Badge bg="warning" style={styles.badge}>AI-Powered</Badge>
                                                Advanced Analytics
                                            </div>
                                            <div style={styles.featureItem}>
                                                <Badge bg="warning" style={styles.badge}>Comprehensive</Badge>
                                                DNA Breakdown
                                            </div>
                                            <div style={styles.featureItem}>
                                                <Badge bg="warning" style={styles.badge}>Coming Soon!</Badge>
                                                Stay tuned for updates
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline-warning"
                                            size="lg"
                                            style={styles.button}
                                            className="w-100"
                                            disabled
                                        >
                                            Coming Soon
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </motion.div>
                        </Col>
                    </Row>
                </Container>

                {/* Call to Action Section */}
                <Container className="mb-5">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true }}
                    >
                        <div style={styles.cta} className="text-center">
                            <h2 style={styles.cardTitle}>Ready to get started?</h2>
                            <p style={styles.cardText}>
                                Explore our services and let us help you take your DNA analysis to the next level!
                            </p>
                            <Button
                                variant="primary"
                                size="lg"
                                as={Link}
                                to="/contact_us"
                                style={styles.button}
                                className="shadow-lg"
                            >
                                Contact Us
                            </Button>
                        </div>
                    </motion.div>
                </Container>
            </Container>
        </>
    );
};

export default OurServicesPage;
